"use client";

import React, { useCallback, useEffect, useMemo, useState } from "react";
import ProductCardList from "@/components/ProductCardList";
import ProductCardListExpandableFooter from "@/components/ProductCardListWrapper/variants/ProductCardListBySearchTab/ProductCardListExpandableFooter";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { ElementClicked } from "@/hooks/eventing/types/redventures.usertracking.v3.ElementClicked";
import { ElementType, FormatSubType } from "@/constants/tagular/main";
import makeEvent from "@/hooks/eventing/makeEvent";
import { EVENTS } from "@/hooks/eventing/types/events";
import { ElementViewed } from "@/hooks/eventing/types/redventures.usertracking.v3.ElementViewed";
import { hyphenateForTagular } from "@/utils/tagular/helpers";
import type {
  Category,
  ProductCardListBySearchTabProps,
  Subcategory,
} from "@/components/ProductCardListWrapper/variants/ProductCardListBySearchTab/types";
import { CategoryTabBar } from "@/components/ProductCardListWrapper/variants/ProductCardListBySearchTab/CategoryTabBar";
import { SubcategoryTabBar } from "@/components/ProductCardListWrapper/variants/ProductCardListBySearchTab/SubcategoryTabBar";
import { createSearchTabValue } from "@/components/ProductCardListWrapper/variants/ProductCardListBySearchTab/utils/createSearchTabValue";
import useIsMobile from "@/hooks/useIsMobile";
import { sanitizeTabValue } from "@/utils/sanitizeTabValue";
import { cn } from "@/lib/utils";
import { Flame } from "lucide-react";
import { getFilteredTrendingCategories } from "@/components/ProductCardListWrapper/variants/ProductCardListBySearchTab/utils/getFilteredTrendingCategories";
import TrendingProductCardListSkeleton from "@/components/ProductCardListWrapper/variants/ProductCardListBySearchTab/TrendingProductCardListSkeleton";

function ProductCardListBySearchTabClient({
  title,
  className,
  categories,
  location,
  cardFormatSubtype = FormatSubType.HorizontallyStacked,
  categoryKeyOrder = [],
  prioritizeCoursesFirst = false,
  showTrendingFlameIcon = false,
  isLoading = false,
  expandableFooter = {
    maxExpandedRows: 2,
    ctaLabel: "Explore more learning options",
  },
}: ProductCardListBySearchTabProps) {
  const filteredCategories = useMemo(
    () =>
      getFilteredTrendingCategories(
        categories,
        categoryKeyOrder,
        prioritizeCoursesFirst,
      ),
    [categories, prioritizeCoursesFirst, categoryKeyOrder],
  );

  const [activeCategory, setActiveCategory] = useState<Category | undefined>(
    undefined,
  );

  const [activeSubcategory, setActiveSubcategory] = useState<
    Subcategory | undefined
  >(undefined);

  const isMobile = useIsMobile();

  const initialVisibleProductCount = isMobile ? 1 : 4;

  useEffect(() => {
    const first = filteredCategories[0];
    setActiveCategory(first);
    setActiveSubcategory(first?.subcategories?.[0]);
  }, [filteredCategories]);

  const [openAccordions, setOpenAccordions] = useState<Record<string, boolean>>(
    {},
  );

  const createAccordionKey = useCallback(
    (categoryLabel: string, subcategoryLabel: string) => {
      return `${createSearchTabValue(categoryLabel, location)}::${sanitizeTabValue(
        subcategoryLabel,
      )}`;
    },
    [location],
  );

  const handleCategoryChange = (sanitizedValue: string) => {
    setOpenAccordions({});
    const category = filteredCategories?.find(
      (cat) =>
        createSearchTabValue(cat.categoryLabel, location) === sanitizedValue,
    );
    setActiveCategory(category);
    setActiveSubcategory(category?.subcategories?.[0]);
  };

  const hasMoreProducts = (
    products: unknown[],
    initialDisplayCount: number,
  ) => {
    return Array.isArray(products) && products.length > initialDisplayCount;
  };

  const shouldShowAccordion = (subcategory: Subcategory | undefined) => {
    return hasMoreProducts(
      subcategory?.products || [],
      initialVisibleProductCount,
    );
  };

  const generateEventData = useCallback(
    (text: string) => {
      const webElement: ElementClicked["webElement"] = {
        elementType: ElementType.Link,
        htmlId: "view-more-link",
        location: location ?? "trending-products",
        position: "1",
        name: hyphenateForTagular(text),
        text: text,
      };
      return { webElement };
    },
    [location],
  );

  const sendViewedEvent = useCallback(
    (title: string) => {
      const event = makeEvent<ElementViewed, EVENTS>(generateEventData(title));
      event<ElementViewed>(EVENTS.ElementViewed)();
    },
    [generateEventData],
  );

  const sendClickedEvent = useCallback(
    (title: string) => {
      const event = makeEvent<ElementClicked, EVENTS>(generateEventData(title));
      event<ElementClicked>(EVENTS.ElementClicked)();
    },
    [generateEventData],
  );

  return (
    <div
      className={cn(
        "py-8 lg:py-20 mx-break-out flex justify-center bg-putty-100",
        className,
      )}
    >
      <div className="container">
        <h2 className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xl lg:text-3xl font-bold mb-4">
          {showTrendingFlameIcon && (
            <span className="inline-flex shrink-0 items-center">
              <Flame
                className="size-7 text-[#E85D32] lg:size-9"
                strokeWidth={1.75}
                aria-hidden
              />
            </span>
          )}
          <span>{title}</span>
        </h2>

        <Tabs
          value={
            activeCategory
              ? createSearchTabValue(activeCategory.categoryLabel, location)
              : undefined
          }
          onValueChange={handleCategoryChange}
        >
          <CategoryTabBar categories={filteredCategories} location={location} />

          {isLoading ? (
            <div className={cn("p-4 mt-0 px-0 rounded-b-xl")}>
              <TrendingProductCardListSkeleton cardCount={4} />
            </div>
          ) : activeCategory ? (
            <TabsContent
              value={createSearchTabValue(
                activeCategory.categoryLabel,
                location,
              )}
              className={cn("p-4 mt-0 px-0 rounded-b-xl")}
            >
              <Tabs
                key={createSearchTabValue(
                  activeCategory.categoryLabel,
                  location,
                )}
                value={
                  activeSubcategory
                    ? sanitizeTabValue(activeSubcategory.subcategoryLabel)
                    : undefined
                }
                onValueChange={(sanitizedSubVal) => {
                  const originalSubcategory =
                    activeCategory.subcategories?.find(
                      (sub) =>
                        sanitizeTabValue(sub.subcategoryLabel) ===
                        sanitizedSubVal,
                    );
                  if (originalSubcategory) {
                    setActiveSubcategory(originalSubcategory);
                  }
                }}
              >
                <SubcategoryTabBar
                  subcategories={activeCategory.subcategories}
                  categoryLabel={activeCategory.categoryLabel}
                >
                  {(subcategory) => {
                    const accordionKey = createAccordionKey(
                      activeCategory.categoryLabel,
                      subcategory.subcategoryLabel,
                    );
                    const isAccordionOpen =
                      openAccordions[accordionKey] ?? false;
                    const accordionTitle = isAccordionOpen
                      ? `Show less ${activeCategory.categoryLabel.toLowerCase()}`
                      : `View more ${activeCategory.categoryLabel.toLowerCase()}`;

                    const COLS_PER_ROW = 4;
                    const MOBILE_MAX_PRODUCTS = 4;
                    const desktopSliceEnd =
                      expandableFooter.maxExpandedRows * COLS_PER_ROW * 2;
                    const allProducts = subcategory.products || [];
                    const products = allProducts.slice(
                      0,
                      initialVisibleProductCount,
                    );
                    const subProducts = allProducts.slice(
                      initialVisibleProductCount,
                      isMobile ? MOBILE_MAX_PRODUCTS : desktopSliceEnd,
                    );

                    return (
                      <>
                        <ProductCardList
                          products={products}
                          location={location}
                          cardFormatSubType={cardFormatSubtype}
                          shouldScroll={false}
                        />
                        {shouldShowAccordion(subcategory) && (
                          <ProductCardListExpandableFooter
                            isExpanded={isAccordionOpen}
                            onToggle={(label) => {
                              setOpenAccordions((prev) => ({
                                ...prev,
                                [accordionKey]: !isAccordionOpen,
                              }));
                              sendClickedEvent(label);
                            }}
                            toggleLabel={accordionTitle}
                            searchCtaLabel={
                              subcategory.ctaLabel ??
                              expandableFooter.ctaLabel ??
                              "Explore more learning options"
                            }
                            searchCtaUrl={
                              subcategory.ctaUrl ??
                              expandableFooter.ctaUrl ??
                              subcategory.searchUrl
                            }
                            location={location}
                            onViewed={(label) => sendViewedEvent(label)}
                          >
                            <ProductCardList
                              className="pt-0"
                              products={subProducts || []}
                              location={location}
                              shouldScroll={false}
                            />
                          </ProductCardListExpandableFooter>
                        )}
                      </>
                    );
                  }}
                </SubcategoryTabBar>
              </Tabs>
            </TabsContent>
          ) : null}
        </Tabs>
      </div>
    </div>
  );
}

export default ProductCardListBySearchTabClient;
